import { fetchAction } from '../utilities'
import { createTurnstile } from '../turnstile'

const INVITE_ACTION = '/actions/expansion/turkey-rot/invite'

const GENERIC_ERROR = 'Something went wrong. Please try again.'

/**
 * Turkey Rot team page: copy the share link, and invite more people.
 *
 * The server also gates the invite endpoint on the invite cookie, so hiding the
 * modal here is presentation only — not the security boundary.
 */
export const teamInvite = (config = {}) => ({
	token: config.token ?? '',
	shareUrl: config.shareUrl ?? '',
	csrfToken: config.csrfToken ?? '',
	turnstileSiteKey: config.turnstileSiteKey ?? '',

	open: false,
	copied: false,
	copyFailed: false,
	email: '',
	teamEmails: '',
	loading: false,
	error: null,
	message: '',

	turnstile: null,
	copiedTimer: null,

	get copyLabel() {
		if (this.copied) {
			return 'Copied!'
		}

		if (this.copyFailed) {
			return 'Copy failed'
		}

		return 'Copy invite link'
	},

	async copy() {
		let copied = false

		try {
			await navigator.clipboard.writeText(this.shareUrl)
			copied = true
		} catch (error) {
			// The Clipboard API needs a secure context and permission. There's no
			// visible input to fall back to selecting, so use a throwaway one.
			copied = this.legacyCopy()
		}

		this.copied = copied
		this.copyFailed = !copied

		if (this.copiedTimer) {
			window.clearTimeout(this.copiedTimer)
		}

		this.copiedTimer = window.setTimeout(() => {
			this.copied = false
			this.copyFailed = false
		}, 2500)
	},

	/**
	 * Last resort for browsers without the Clipboard API, or when permission is
	 * refused. Returns whether it actually copied, so the button never claims
	 * success it didn't have.
	 */
	legacyCopy() {
		try {
			const field = document.createElement('textarea')

			field.value = this.shareUrl
			field.setAttribute('readonly', '')
			field.style.position = 'fixed'
			field.style.top = '0'
			field.style.opacity = '0'

			document.body.appendChild(field)
			field.select()

			const copied = document.execCommand('copy')

			document.body.removeChild(field)

			return copied
		} catch (error) {
			return false
		}
	},

	async submit() {
		if (this.loading) {
			return
		}

		if (this.teamEmails.trim() === '') {
			this.error = 'emails'
			this.message = 'Please enter at least one email address.'

			return
		}

		this.loading = true
		this.error = null
		this.message = ''

		const turnstileToken = await this.turnstile(this.$refs.turnstile)

		const response = await fetchAction(
			INVITE_ACTION,
			'POST',
			this.csrfToken,
			JSON.stringify({
				token: this.token,
				email: this.email.trim() || null,
				teamEmails: this.teamEmails,
				turnstileToken,
			}),
		)

		this.loading = false

		if (!response.isSuccessful) {
			this.error = 'network'
			this.message = GENERIC_ERROR

			return
		}

		const data = response.successData ?? {}

		if (!data.success) {
			this.error = 'request'
			this.message = data.message ?? GENERIC_ERROR

			return
		}

		this.message = data.message ?? 'Invites sent!'
		this.teamEmails = ''

		// Give the reader a moment to see the confirmation, then reload so the
		// roster and count reflect anything that changed.
		window.setTimeout(() => {
			window.location.reload()
		}, 1800)
	},

	init() {
		this.turnstile = createTurnstile(this.turnstileSiteKey)
	},

	destroy() {
		if (this.copiedTimer) {
			window.clearTimeout(this.copiedTimer)
		}
	},
})
